/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useState } from 'react';
import MoreFilter from '../../../components/shared/MoreFilter';
import InputField from '../../../components/shared/InputField';
import SearchIcon from '../../../components/icons/SearchIcon';
import * as yup from 'yup';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup/dist/yup';
import Button from '../../../components/shared/Button';
import { Typo } from '../../../components/Typo';
import { FormattedMessage, useIntl } from 'react-intl';
import { TRANSLATED_CONSTANTS } from '../../../static/translatedConstants';
import { useAppSelector } from '../../../lib/redux/hooks';
import {
  addFilter,
  addFilterModal,
  addLatLng,
  requestFly,
  setAutoWidened,
  setMovedAway
} from '../../../lib/redux/reducers/filter.slice';
import { useDispatch } from 'react-redux';
import LocationDropdown from '../../../components/shared/location Dropdown/LocationDropdown';
import { LocationBound, LocationType } from '../../../model/Location';
import { useGetFilterData } from '../../../hooks/marketPlace/useGetFilterData';
import { DEFAULT_LAT_LNG, DEFAULT_LOCATION_BOUNDS } from '../../../constants/data';
import { alertnatePriceOptions, dataTypes, priceType } from '../../../util/MoreFilterUtil';

const Filters = () => {
  const dispatch = useDispatch();

  const intl = useIntl();
  const { itemConditions } = useGetFilterData();
  const filterFromModal = useAppSelector((state) => state.filter.filterModal);
  const defaultSearchText = filterFromModal.searchText || '';

  const clearFilterState = useAppSelector((state) => state.filter.toBeCleared);
  const [locationData, setLocationData] = useState<LocationType | any>(
    filterFromModal.locationData
  );
  const [locationBounds, setLocationBounds] = useState<LocationBound>(
    filterFromModal.locationBounds
  );
  const [latLng, setLatLng] = useState<any>(DEFAULT_LAT_LNG);
  const [resetState, setOnResetState] = useState({ state: false, key: 0 });
  const [moreFilter, setMoreFilter] = useState<any>();
  const [shippableOnly, setShippableOnly] = useState<boolean>(
    !!(filterFromModal?.moreFilter as any)?.shippableOnly
  );

  const onLocationSelect = (
    locationData: LocationType,
    locationBounds: LocationBound,
    latLng: any
  ) => {
    setLocationData(locationData);
    setLocationBounds(locationBounds);
    setLatLng(latLng);
  };

  // One-step location search (map plan C1): picking a suggestion (or "Current
  // Location") flies the map immediately instead of waiting for the Search
  // button. Uses the fresh ARGUMENTS, not the useStates above (those haven't
  // committed yet). handleReset keeps calling plain onLocationSelect - it
  // dispatches its own cleared filterModal right after, so no double fly.
  const onLocationPicked = (
    locationData: LocationType,
    locationBounds: LocationBound,
    latLng: any
  ) => {
    onLocationSelect(locationData, locationBounds, latLng);
    dispatch(setAutoWidened(false));
    dispatch(setMovedAway(false));
    dispatch(
      addFilterModal({
        filterModal: {
          ...filterFromModal,
          locationData: locationData,
          locationBounds: locationBounds,
          address: locationData?.address ?? '',
          isFirstTime: false
        }
      })
    );
    dispatch(addLatLng({ latLng }));
    dispatch(requestFly());
  };

  useEffect(() => {
    if (clearFilterState) {
      handleReset();
      dispatch(addFilter({ state: false }));
    }
  }, [clearFilterState]);

  // Chips on the results header toggle types straight into redux (map plan
  // E3). Mirror redux back into the local copy so a later Search press sends
  // the chip choices instead of clobbering them with stale local state.
  useEffect(() => {
    setMoreFilter(filterFromModal?.moreFilter ?? undefined);
  }, [filterFromModal?.moreFilter]);

  //if map moves dropdown location changes to empty location
  useEffect(() => {
    if (filterFromModal.locationBounds !== locationBounds) {
      setLocationData(filterFromModal.locationData);
      setLocationBounds(filterFromModal.locationBounds);
    }
  }, [filterFromModal]);

  const searchFilter = yup.object().shape({
    searchInput: yup.string()
  });
  const { register, reset, handleSubmit, setValue } = useForm({
    resolver: yupResolver(searchFilter)
  });

  // F7 round 4: the map view's floating bar can change the keyword while
  // this component sits css-hidden (it stays MOUNTED, so defaultValue never
  // re-applies). Without this sync the list input shows a stale/empty value
  // and its next Search submit silently WIPES the map-applied keyword.
  useEffect(() => {
    setValue('searchInput', filterFromModal.searchText || '');
  }, [filterFromModal.searchText, setValue]);

  const handleReset = () => {
    dispatch(setAutoWidened(false));
    dispatch(setMovedAway(false));
    setOnResetState({ state: true, key: Math.random() });
    reset();
    setShippableOnly(false);
    const latLngData = {
      lat: DEFAULT_LAT_LNG.lat, // Set your default latitude here
      lng: DEFAULT_LAT_LNG.lng, // Set your default longitude here
      zoom: DEFAULT_LAT_LNG.zoom // Set your default zoom level here latLngData
    };
    onLocationSelect(
      {
        id: '',
        address: '',
        latitude: 0,
        longitude: 0,
        countryCode: ''
      },
      DEFAULT_LOCATION_BOUNDS,
      latLngData
    );
    dispatch(
      addFilterModal({
        filterModal: {
          searchText: '',
          categories: null,
          locationData: null,
          locationBounds: DEFAULT_LOCATION_BOUNDS,
          address: ''
        }
      })
    );

    dispatch(
      addLatLng({
        latLng: latLngData
      })
    );
    // Fly back to the default world view (the address no longer changes on
    // pans, so the fly must be requested explicitly - map plan C2).
    dispatch(requestFly());
  };

  const onSubmit = (data: any) => {
    const searchText = data?.searchInput;
    handleSearch(searchText, moreFilter);
  };

  const handleSearch = (searchText: string, moreFilter: any, categoriesOverride?: any[]) => {
    // A deliberate search dismisses the "showing everything on the map" note
    // and re-anchors the headline to the chosen place.
    dispatch(setAutoWidened(false));
    dispatch(setMovedAway(false));
    dispatch(addFilter({ searchText: searchText }));
    dispatch(
      addFilterModal({
        filterModal: {
          moreFilter: moreFilter ?? undefined,
          searchText: searchText ?? '',
          // Category discovery P0: category state has ONE channel now -
          // filterModal.categories, owned by the chip bar and the sheet's
          // chip group ([] = All = no category filter). A keyword search
          // passes the active selection through instead of clobbering it.
          categories: categoriesOverride ?? filterFromModal?.categories ?? [],
          locationData: locationData,
          locationBounds: locationBounds,
          address: locationData?.address ?? '',
          isFirstTime: false
        }
      })
    );
    dispatch(addLatLng({ latLng }));
    dispatch(requestFly());
  };

  const onToggleShippable = () => {
    const next = !shippableOnly;
    setShippableOnly(next);
    const merged = { ...(moreFilter ?? {}), shippableOnly: next };
    setMoreFilter(merged);
    handleSearch(defaultSearchText, merged);
  };

  // F3: mobile-only refinement badge on the Filters button - how many facet
  // groups deviate from "everything".
  const activeFilterCount =
    (moreFilter?.listings && moreFilter.listings.length < dataTypes.length ? 1 : 0) +
    (moreFilter?.conditions?.length ? 1 : 0) +
    (moreFilter?.alternatePriceOptions?.length ? 1 : 0) +
    (moreFilter?.priceType?.length ? 1 : 0) +
    (shippableOnly ? 1 : 0) +
    (filterFromModal?.categories?.length ? 1 : 0);

  // F3: below md the search input collapses behind an icon button so the
  // filter bar fits one row.
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);

  return (
    <div className="mt-8 w-full ">
      <div className="flex gap-3 w-full mb-4">
        <div className="w-full md:flex-1 md:gap-3 flex">
          <div className="w-full md:flex-1">
            {/* Category discovery P1: the old md+ inline category dropdown
                that sat beside this is retired - the persistent chip bar
                below the search row is the category control on every
                breakpoint (one mental model, one redux channel). */}
            <LocationDropdown
              placeholder={intl.formatMessage({
                id: 'page.marketplace.city_neighborhood_placeholder',
                defaultMessage: 'City, Neighborhood...'
              })}
              location={locationData}
              onLocationSelect={onLocationPicked}></LocationDropdown>
          </div>
        </div>
        <div className="relative min-w-0 md:min-w-[140px]">
          <MoreFilter
            key={resetState.key}
            resetState={resetState.state}
            type="map"
            sheet={true}
            shippableOnly={shippableOnly}
            onToggleShippable={onToggleShippable}
            listings={dataTypes}
            conditions={itemConditions}
            onFilter={(filter) => {
              // Categories ride ALONGSIDE moreFilter, not inside it - the
              // filterModal.categories channel is shared with the chip bar.
              const { categories: pickedCategories, ...restFilter } = filter ?? {};
              const merged = { ...restFilter, shippableOnly };
              setMoreFilter(merged);
              handleSearch(defaultSearchText, merged, pickedCategories);
            }}
            priceType={priceType}
            alertnatePriceOptions={alertnatePriceOptions}
          />
          {activeFilterCount > 0 && (
            <span className="pointer-events-none absolute -end-1 -top-1 grid h-5 min-w-[20px] place-items-center rounded-full bg-yellow-400 px-1 text-xs font-bold text-gray-900 md:hidden">
              {activeFilterCount}
            </span>
          )}
        </div>
        {/* F3: mobile search toggle keeps the bar to one row. */}
        <button
          type="button"
          onClick={() => setMobileSearchOpen((open) => !open)}
          aria-expanded={mobileSearchOpen}
          className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg border border-gray-300 bg-white text-gray-600 hover:bg-gray-50 md:hidden">
          <SearchIcon className="h-5 w-5" />
        </button>
      </div>
      {/* F3: on mobile this chip lives inside the Filters sheet instead. */}
      <div className="hidden md:flex mb-4">
        <button
          type="button"
          onClick={onToggleShippable}
          className={
            'rounded-full border px-3 py-1.5 text-sm font-semibold ' +
            (shippableOnly
              ? 'border-yellow-400 bg-yellow-50 text-yellow-700'
              : 'border-gray-300 text-gray-600 hover:bg-gray-50')
          }>
          <FormattedMessage
            id="page.marketplace.buy_ship_available"
            defaultMessage="Buy & ship available"
          />
        </button>
      </div>
      <form
        onSubmit={handleSubmit(onSubmit)}
        className={mobileSearchOpen ? 'block' : 'hidden md:block'}>
        <div className="flex gap-3">
          <InputField
            prefix={<SearchIcon className="h-5 w-5 text-gray-500" />}
            name="searchInput"
            label=""
            defaultValue={defaultSearchText}
            placeholder={intl.formatMessage({
              id: TRANSLATED_CONSTANTS.SEARCH_BY_ITEM_NAME.id,
              defaultMessage: TRANSLATED_CONSTANTS.SEARCH_BY_ITEM_NAME.defaultMessage
            })}
            type="text"
            register={register}
          />
          <div className="md:block">
            <div className="flex gap-x-3">
              <Button onClick={handleReset} theme="base" size="lg">
                <Typo.textMd fontWeight="font-semibold" className="text-gray-700">
                  <FormattedMessage
                    id={TRANSLATED_CONSTANTS.CLEAR.id}
                    defaultMessage={TRANSLATED_CONSTANTS.CLEAR.defaultMessage}
                  />
                </Typo.textMd>
              </Button>
              <Button type="submit" theme="primary-yellow" size="lg">
                <Typo.textMd fontWeight="font-semibold" className="text-gray-700">
                  <FormattedMessage
                    id={TRANSLATED_CONSTANTS.SEARCH.id}
                    defaultMessage={TRANSLATED_CONSTANTS.SEARCH.defaultMessage}
                  />
                </Typo.textMd>
              </Button>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
};

export default Filters;
